import { useState } from 'react';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../../../lib/firebase/config';

interface PasswordChangeFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface UsePasswordChangeResult {
  isLoading: boolean;
  error: string | null;
  success: boolean;
  changePassword: (data: PasswordChangeFormData) => Promise<boolean>;
  resetState: () => void;
}

export function usePasswordChange(): UsePasswordChangeResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetState = () => {
    setError(null);
    setSuccess(false);
    setIsLoading(false);
  };

  const validatePasswordData = (data: PasswordChangeFormData): string | null => {
    if (!data.currentPassword || !data.newPassword || !data.confirmPassword) {
      return 'All fields are required';
    }

    if (data.newPassword.length < 6) {
      return 'New password must be at least 6 characters';
    }

    if (data.newPassword !== data.confirmPassword) {
      return 'New passwords do not match';
    }

    if (data.newPassword === data.currentPassword) {
      return 'New password must be different from current password';
    }

    return null;
  };

  const changePassword = async (data: PasswordChangeFormData): Promise<boolean> => {
    setError(null);
    setSuccess(false);
    
    // Validate input data
    const validationError = validatePasswordData(data);
    if (validationError) {
      setError(validationError);
      return false;
    }

    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('User not found');
      }

      // Reauthenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, data.currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, data.newPassword);

      setSuccess(true);
      return true;
    } catch (error: any) {
      let errorMessage = 'Failed to change password';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please sign out and sign back in, then try again';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    success,
    changePassword,
    resetState
  };
} 